import { queryClient } from '@/queries/client';

import { importEntities } from './import-entities';
import { beginStatusRead } from './status-observations';

import type { Status } from 'pl-api';

// Reject intervening streams and newer reads, including nested boosted originals.
// Neither edited_at nor monotonic counters establish snapshot ordering.
const trackStatusUpdates = (scope: string, signal?: AbortSignal) => {
  const removed = new Set<string>();
  const read = beginStatusRead(scope);
  const unwatch = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'removed') return;
    const key = event.query.queryKey;
    if (
      key.length === 3 &&
      key[0] === scope &&
      key[1] === 'statuses' &&
      typeof key[2] === 'string'
    ) {
      removed.add(key[2]);
    }
  });
  const stop = () => {
    unwatch();
    signal?.removeEventListener('abort', stop);
  };
  if (signal?.aborted) stop();
  else signal?.addEventListener('abort', stop, { once: true });
  return {
    ...read,
    stop,
    isStatusCurrent: (id: string) => !removed.has(id) && read.isStatusCurrent(id),
  };
};

const fetchAndImportStatuses = async <T>(
  scope: string,
  fetcher: () => Promise<T>,
  select: (response: T) => Status[],
): Promise<T> => {
  const guard = trackStatusUpdates(scope);
  try {
    const response = await fetcher();
    guard.stop();
    const statuses = select(response);
    if (statuses.length)
      importEntities(
        scope,
        { statuses },
        {
          withParents: true,
          isStatusCurrent: guard.isStatusCurrent,
          onStatusImported: guard.didImportStatus,
        },
      );
    return response;
  } finally {
    guard.stop();
  }
};

export { fetchAndImportStatuses, trackStatusUpdates };
